import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import {
  ApiError,
  Case,
  CaseMemberRole,
  CaseUser,
  User,
  api,
} from "../lib/api";
import { useAuth } from "../lib/auth";

interface Props {
  caseId: number;
  /** The current case record. We read `my_role` from it to decide whether
   *  the panel renders edit controls; owners/admins always can. */
  kase: Case;
  /** Bumped by the parent to force a re-fetch (e.g. after the case row
   *  itself is reloaded so member_count and my_role line up). */
  refreshKey?: number;
  /** Fired whenever the membership set changes (add/remove/role change) so
   *  the parent can refresh the case (member_count, my_role). */
  onChanged?: () => void;
}

const ROLE_OPTIONS: Array<{
  value: CaseMemberRole;
  label: string;
  help: string;
}> = [
  {
    value: "lead",
    label: "Lead",
    help: "Manages case members alongside org owners/admins.",
  },
  {
    value: "collaborator",
    label: "Collaborator",
    help: "Full read/write on the case but cannot manage members.",
  },
  {
    value: "reviewer",
    label: "Reviewer",
    help: "Read/write on notes and chat; can review work product.",
  },
  {
    value: "viewer",
    label: "Viewer",
    help: "Read-only access to the case.",
  },
];

const ROLE_LABEL: Record<CaseMemberRole, string> = ROLE_OPTIONS.reduce(
  (m, r) => ({ ...m, [r.value]: r.label }),
  {} as Record<CaseMemberRole, string>,
);

export default function MembersView({
  caseId,
  kase,
  refreshKey = 0,
  onChanged,
}: Props) {
  const me = useAuth((s) => s.user);
  const [members, setMembers] = useState<CaseUser[] | null>(null);
  const [orgUsers, setOrgUsers] = useState<User[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [busyUserId, setBusyUserId] = useState<number | null>(null);
  const [showAdd, setShowAdd] = useState(false);

  // The current user can manage members iff they're an org owner/admin OR
  // they hold the `lead` role on this specific case. The server enforces
  // the same rule on POST/DELETE /cases/:id/users — this is purely UI gating.
  const canManage =
    !!me &&
    (me.role === "owner" || me.role === "admin" || kase.my_role === "lead");

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const list = await api.get<CaseUser[]>(`/cases/${caseId}/users`);
      setMembers(list);
    } catch (e: unknown) {
      setErr(e instanceof ApiError ? e.message : "Failed to load members");
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  // Lazy-load the org user list the first time the manager opens the
  // "Add member" picker — saves a roundtrip for users who only browse.
  const loadOrgUsers = useCallback(async () => {
    if (orgUsers) return;
    try {
      const list = await api.get<User[]>("/users");
      setOrgUsers(list);
    } catch (e: unknown) {
      setErr(e instanceof ApiError ? e.message : "Failed to load users");
    }
  }, [orgUsers]);

  function flashFor(msg: string) {
    setFlash(msg);
    setTimeout(() => setFlash((m) => (m === msg ? null : m)), 3000);
  }

  async function handleRoleChange(userId: number, role: CaseMemberRole) {
    setBusyUserId(userId);
    setErr(null);
    try {
      await api.post(`/cases/${caseId}/users`, { user_id: userId, role });
      setMembers((cur) =>
        (cur ?? []).map((m) => (m.id === userId ? { ...m, role } : m)),
      );
      flashFor(`Updated role to ${ROLE_LABEL[role]}.`);
      onChanged?.();
    } catch (e: unknown) {
      setErr(e instanceof ApiError ? e.message : "Failed to update role");
    } finally {
      setBusyUserId(null);
    }
  }

  async function handleRemove(userId: number, displayName: string) {
    if (
      !window.confirm(
        `Remove ${displayName} from this case? They'll lose access immediately.`,
      )
    ) {
      return;
    }
    setBusyUserId(userId);
    setErr(null);
    try {
      await api.del(`/cases/${caseId}/users/${userId}`);
      setMembers((cur) => (cur ?? []).filter((m) => m.id !== userId));
      flashFor(`${displayName} removed from this case.`);
      onChanged?.();
    } catch (e: unknown) {
      setErr(e instanceof ApiError ? e.message : "Failed to remove member");
    } finally {
      setBusyUserId(null);
    }
  }

  async function handleAdd(userId: number, role: CaseMemberRole) {
    setErr(null);
    try {
      await api.post(`/cases/${caseId}/users`, { user_id: userId, role });
      setShowAdd(false);
      // Refetch to get the canonical assigned_at + display_name from the
      // join. Cheaper than reconciling the org-user record into a CaseUser
      // shape on the client.
      await load();
      flashFor("Member added.");
      onChanged?.();
    } catch (e: unknown) {
      setErr(e instanceof ApiError ? e.message : "Failed to add member");
    }
  }

  // Render: empty placeholder until the first fetch completes so we don't
  // flash an "owner-less" view before we know who's on it.
  if (loading && !members) {
    return <div className="muted">Loading members…</div>;
  }

  const sortedMembers = [...(members ?? [])].sort((a, b) => {
    // Leads first, then alphabetical — matches the rest of the product
    // (admin chips bubble up for the same reason).
    if (a.role === "lead" && b.role !== "lead") return -1;
    if (a.role !== "lead" && b.role === "lead") return 1;
    return a.display_name.localeCompare(b.display_name);
  });

  const memberIds = new Set((members ?? []).map((m) => m.id));
  const eligibleToAdd = (orgUsers ?? []).filter(
    (u) => !memberIds.has(u.id) && u.is_active,
  );

  return (
    <div className="case-members">
      <header className="case-members__head">
        <div className="case-members__head-text">
          <h2 className="case-members__title">Case members</h2>
          <p className="case-members__lead muted">
            {canManage
              ? "Pick who can see and work on this case. Org owners and admins always have access; everyone else needs an explicit assignment."
              : "Everyone with access to this case. The case lead and your org admins can change membership."}
          </p>
        </div>
        {canManage && (
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => {
              setShowAdd(true);
              setErr(null);
              void loadOrgUsers();
            }}
          >
            + Add member
          </button>
        )}
      </header>

      {err && <div className="alert alert--error">{err}</div>}
      {flash && <div className="alert alert--ok">{flash}</div>}

      <div className="admin__scroll">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Case role</th>
              <th>Joined</th>
              {canManage && <th></th>}
            </tr>
          </thead>
          <tbody>
            {sortedMembers.length === 0 && (
              <tr>
                <td className="muted" colSpan={canManage ? 5 : 4}>
                  No members yet. {canManage && "Add a teammate to share access."}
                </td>
              </tr>
            )}
            {sortedMembers.map((m) => {
              const isMe = me?.id === m.id;
              const busy = busyUserId === m.id;
              return (
                <tr key={m.id}>
                  <td>
                    <div className="admin-table__name">
                      {m.display_name}
                      {isMe && (
                        <span className="admin-table__chip admin-table__chip--me">
                          you
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="admin-table__mono">{m.email}</td>
                  <td>
                    {canManage ? (
                      <select
                        className="case-members__role-select"
                        value={m.role}
                        disabled={busy}
                        onChange={(ev) =>
                          void handleRoleChange(
                            m.id,
                            ev.target.value as CaseMemberRole,
                          )
                        }
                      >
                        {ROLE_OPTIONS.map((r) => (
                          <option key={r.value} value={r.value}>
                            {r.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span className={`admin-table__role admin-table__role--${m.role}`}>
                        {m.role}
                      </span>
                    )}
                  </td>
                  <td className="muted">{formatJoined(m.assigned_at)}</td>
                  {canManage && (
                    <td className="admin-table__actions">
                      <button
                        type="button"
                        className="btn btn--sm"
                        disabled={busy}
                        onClick={() => void handleRemove(m.id, m.display_name)}
                      >
                        {isMe ? "Leave" : "Remove"}
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showAdd && (
        <AddMemberModal
          eligible={eligibleToAdd}
          loading={!orgUsers}
          onClose={() => setShowAdd(false)}
          onSubmit={handleAdd}
        />
      )}
    </div>
  );
}

function AddMemberModal({
  eligible,
  loading,
  onClose,
  onSubmit,
}: {
  eligible: User[];
  loading: boolean;
  onClose: () => void;
  onSubmit: (userId: number, role: CaseMemberRole) => Promise<void>;
}) {
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [role, setRole] = useState<CaseMemberRole>("collaborator");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const sorted = [...eligible].sort((a, b) =>
      a.display_name.localeCompare(b.display_name),
    );
    if (!q) return sorted;
    return sorted.filter(
      (u) =>
        u.display_name.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q) ||
        u.role.toLowerCase().includes(q),
    );
  }, [eligible, search]);

  // Reset the selection if the previously highlighted user falls out of
  // the filtered set (e.g. user typed into the search box).
  useEffect(() => {
    if (selectedId == null) return;
    if (!filtered.some((u) => u.id === selectedId)) setSelectedId(null);
  }, [filtered, selectedId]);

  // Close on Escape; lock body scroll while open.
  useEffect(() => {
    function onKey(ev: KeyboardEvent) {
      if (ev.key === "Escape" && !busy) onClose();
    }
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose, busy]);

  async function handleSubmit(ev: FormEvent) {
    ev.preventDefault();
    if (selectedId == null) return;
    setBusy(true);
    setErr(null);
    try {
      await onSubmit(selectedId, role);
    } catch (e: unknown) {
      setErr(e instanceof ApiError ? e.message : "Failed to add member");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-member-title"
      onClick={(ev) => {
        if (ev.target === ev.currentTarget && !busy) onClose();
      }}
    >
      <form
        className="modal__card modal__card--lg modal__card--split"
        onClick={(ev) => ev.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <header className="modal__header">
          <div className="modal__header-main">
            <h2 id="add-member-title" className="modal__header-title">
              Add member to case
            </h2>
            <p className="muted" style={{ margin: 0, fontSize: 13 }}>
              Anyone in your organization can be added. Pick a role; you can
              change it later from the members list.
            </p>
          </div>
          <button
            type="button"
            className="modal__close"
            onClick={onClose}
            aria-label="Close"
            title="Close (Esc)"
            disabled={busy}
          >
            ×
          </button>
        </header>

        <div className="modal__body">
          {err && <div className="alert alert--error">{err}</div>}

          <input
            type="search"
            placeholder="Search by name, email, or account role…"
            value={search}
            onChange={(ev) => setSearch(ev.target.value)}
            autoFocus
            className="case-members__search"
            disabled={busy}
          />

          <div className="case-members__picker" role="listbox" aria-label="Eligible users">
            {loading && <div className="muted">Loading users…</div>}
            {!loading && filtered.length === 0 && (
              <div className="muted">
                {search.trim()
                  ? "No matching users in your organization."
                  : "Everyone in your organization is already on this case."}
              </div>
            )}
            {!loading &&
              filtered.map((u) => {
                const active = selectedId === u.id;
                return (
                  <button
                    key={u.id}
                    type="button"
                    role="option"
                    aria-selected={active}
                    className={`case-members__picker-row${
                      active ? " case-members__picker-row--active" : ""
                    }`}
                    onClick={() => setSelectedId(u.id)}
                  >
                    <span className="case-members__picker-name">
                      {u.display_name}
                    </span>
                    <span className="case-members__picker-email muted">
                      {u.email}
                    </span>
                    <span
                      className={`admin-table__role admin-table__role--${u.role}`}
                    >
                      {u.role}
                    </span>
                  </button>
                );
              })}
          </div>

          <div className="case-members__role-row">
            <label htmlFor="add-member-role">Case role</label>
            <select
              id="add-member-role"
              value={role}
              onChange={(ev) => setRole(ev.target.value as CaseMemberRole)}
              disabled={busy}
            >
              {ROLE_OPTIONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
            <span className="case-members__role-help muted">
              {ROLE_OPTIONS.find((r) => r.value === role)?.help}
            </span>
          </div>
        </div>

        <footer className="modal__footer">
          <span className="muted modal__footer-hint">
            {selectedId == null ? "Pick a user to add." : "Ready to add."}
          </span>
          <div className="modal__footer-actions">
            <button
              type="button"
              className="btn"
              onClick={onClose}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn--primary"
              disabled={busy || selectedId == null}
            >
              {busy ? "Adding…" : "Add to case"}
            </button>
          </div>
        </footer>
      </form>
    </div>
  );
}

function formatJoined(iso?: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${mm}/${dd}/${d.getFullYear()}`;
}
